import { defineStore } from "pinia";
import axios from "axios";
import apiService from "@/services/api";
import { useToast } from "vue-toastification";
// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];
let isLoggingOut = false;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    token: null,
    isLoading: false,
    initialized: false,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    isAdmin: (state) => state.user?.role === "admin",
    userProfile: (state) => state.user,
  },

  actions: {
    // Initialize auth state from localStorage
    initAuth() {
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");

      if (token && user) {
        this.token = token;
        this.user = JSON.parse(user);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        this.setupAxiosInterceptors();
      }
    },

    // Setup axios interceptors for automatic token refresh
    setupAxiosInterceptors() {
      // Response interceptor
      axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          const originalRequest = error.config;

          if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
              // If refresh is already in progress, queue the request
              return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              })
                .then((token) => {
                  originalRequest.headers["Authorization"] = `Bearer ${token}`;
                  return axios(originalRequest);
                })
                .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
              // Try to refresh the token
              const data = await apiService.post(
                "/api/handlers?module=auth&action=refresh"
              );

              const { token } = data.data;

              // Update token in state and localStorage
              this.token = token;
              localStorage.setItem("token", token);
              axios.defaults.headers.common[
                "Authorization"
              ] = `Bearer ${token}`;

              // Process queued requests
              failedQueue.forEach(({ resolve }) => resolve(token));
              failedQueue = [];

              // Retry the original request
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              return axios(originalRequest);
            } catch (refreshError) {
              // Refresh failed — limpar estado diretamente sem chamar logout()
              // (evita loop: logout → 401 → refresh → falha → logout → ...)
              failedQueue.forEach(({ reject }) => reject(refreshError));
              failedQueue = [];
              if (!isLoggingOut) {
                isLoggingOut = true;
                this.user = null;
                this.token = null;
                localStorage.removeItem("token");
                localStorage.removeItem("user");
                delete axios.defaults.headers.common["Authorization"];
                isLoggingOut = false;
              }
              return Promise.reject(refreshError);
            } finally {
              isRefreshing = false;
            }
          }

          return Promise.reject(error);
        }
      );
    },

    // Login user
    async login(credentials) {
      this.isLoading = true;
      const toast = useToast();

      try {
        console.log("🔐 Fazendo login:", {
          email: credentials.email,
          url: "/api/handlers?module=auth&action=login",
          baseURL: axios.defaults.baseURL,
        });

        // apiService.post já retorna response.data diretamente
        const data = await apiService.post(
          "/api/handlers?module=auth&action=login",
          credentials
        );

        console.log("📥 Resposta do login:", {
          data: data,
          hasData: !!data?.data,
          hasUser: !!data?.data?.user,
          hasToken: !!data?.data?.token,
        });

        // Validação defensiva da resposta
        if (!data) {
          throw new Error("Resposta vazia do servidor");
        }

        if (!data.data) {
          throw new Error("Formato de resposta inválido: data ausente");
        }

        const { user, token } = data.data;

        if (!user || !token) {
          throw new Error("Usuário ou token ausente na resposta");
        }

        // Store in state
        this.user = user;
        this.token = token;

        // Store in localStorage
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));

        // Set axios default header
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        console.log("✅ Login bem-sucedido:", user.username);
        toast.success("Login realizado com sucesso!");
        return { success: true };
      } catch (error) {
        console.error("❌ Erro no login:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText,
        });

        // Check if email verification is needed
        if (
          error.response?.status === 403 &&
          error.response?.data?.needsVerification
        ) {
          const message =
            "Sua conta ainda não foi verificada. Verifique seu email.";
          toast.error(message);
          return {
            success: false,
            needsVerification: true,
            email: error.response?.data?.email,
            error: message,
          };
        }

        const message =
          error.response?.data?.message ||
          error.message ||
          "Erro ao fazer login";
        toast.error(message);
        return { success: false, error: message };
      } finally {
        this.isLoading = false;
      }
    },

    // Register user
    async register(userData) {
      this.isLoading = true;
      const toast = useToast();

      try {
        console.log("🚀 Enviando requisição de registro:", userData);
        console.log("📍 URL Base do Axios:", axios.defaults.baseURL);

        // SEMPRE usar JSON para registro (mais compatível com Vercel Functions)
        const requestData = {
          username: userData.username,
          email: userData.email,
          password: userData.password,
        };

        console.log("📄 Enviando registro JSON:", requestData);

        const data = await apiService.post(
          "/api/handlers?module=auth&action=register",
          requestData
        );

        console.log("✅ Resposta do registro:", data);

        // ✅ NOVO FLUXO COM VERIFICAÇÃO DE EMAIL
        // O backend não retorna token - usuário deve verificar email primeiro
        if (data.needsVerification) {
          toast.info(
            data.message ||
              "Cadastro realizado! Verifique seu email para ativar sua conta."
          );
          return {
            success: true,
            needsVerification: true,
            email: data.data?.user?.email,
          };
        }

        // ⚠️ FALLBACK: Se por algum motivo retornar token (fluxo antigo)
        const { user, token } = data.data || {};
        if (token) {
          this.user = user;
          this.token = token;
          localStorage.setItem("token", token);
          localStorage.setItem("user", JSON.stringify(user));
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          toast.success("Conta criada com sucesso!");
        }

        // ❌ REMOVIDO: Upload de imagem durante registro
        // Usuário só pode fazer upload DEPOIS de verificar email e fazer login
        return { success: true };
      } catch (error) {
        console.error("❌ Erro completo:", error);
        console.error("❌ Resposta do erro:", error.response?.data);
        console.error("❌ Status:", error.response?.status);
        console.error("❌ Headers:", error.response?.headers);

        const message = error.response?.data?.message || "Erro ao criar conta";
        const errors = error.response?.data?.errors;

        if (errors && errors.length > 0) {
          errors.forEach((err) => {
            toast.error(`${err.msg || err.message}`);
          });
        } else {
          toast.error(message);
        }

        return { success: false, error: message, details: errors };
      } finally {
        this.isLoading = false;
      }
    },

    // Logout user
    async logout() {
      if (isLoggingOut) return;
      isLoggingOut = true;

      const toast = useToast();

      try {
        await apiService.post("/api/handlers?module=auth&action=logout");
      } catch (error) {
        console.warn("Logout error:", error);
      }

      this.user = null;
      this.token = null;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      delete axios.defaults.headers.common["Authorization"];

      toast.info("Logout realizado com sucesso!");
      isLoggingOut = false;
    },

    // Get user profile
    async fetchProfile() {
      try {
        const data = await apiService.get(
          "/api/handlers?module=auth&action=profile"
        );
        this.user = data.data.user;
        localStorage.setItem("user", JSON.stringify(this.user));
        return { success: true };
      } catch (error) {
        const message = error.response?.data?.message || "Erro ao obter perfil";
        return { success: false, error: message };
      }
    },

    // Update profile
    async updateProfile(profileData) {
      this.isLoading = true;
      const toast = useToast();

      try {
        const data = await apiService.put(
          "/api/handlers?module=auth&action=profile",
          profileData
        );
        this.user = data.data.user;
        localStorage.setItem("user", JSON.stringify(this.user));

        toast.success("Perfil atualizado com sucesso!");
        return { success: true };
      } catch (error) {
        const message =
          error.response?.data?.message || "Erro ao atualizar perfil";
        toast.error(message);
        return { success: false, error: message };
      } finally {
        this.isLoading = false;
      }
    },

    // Update profile image
    async updateProfileImage(imageFile) {
      this.isLoading = true;
      const toast = useToast();

      try {
        console.log("📸 Convertendo imagem para base64...");

        // Converter imagem para base64 (Vercel Functions não suporta multipart)
        const base64Image = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(imageFile);
        });

        console.log("✅ Imagem convertida, enviando para servidor...");

        // Enviar como JSON com base64
        await apiService.post(
          "/api/handlers?module=users&action=profile-image",
          {
            image_base64: base64Image,
          }
        );

        // Update user profile
        await this.fetchProfile();

        toast.success("Foto de perfil atualizada!");
        return { success: true };
      } catch (error) {
        const message =
          error.response?.data?.message || "Erro ao atualizar foto";
        toast.error(message);
        return { success: false, error: message };
      } finally {
        this.isLoading = false;
      }
    },

    // Remove profile image
    async removeProfileImage() {
      this.isLoading = true;
      const toast = useToast();

      try {
        await apiService.delete(
          "/api/handlers?module=users&action=profile-image"
        );

        // Update user profile
        await this.fetchProfile();

        toast.success("Foto de perfil removida!");
        return { success: true };
      } catch (error) {
        const message = error.response?.data?.message || "Erro ao remover foto";
        toast.error(message);
        return { success: false, error: message };
      } finally {
        this.isLoading = false;
      }
    },

    // Verify email with token
    async verifyEmail(token) {
      try {
        const data = await apiService.get(
          `/api/handlers?module=auth&action=verify&token=${token}`
        );

        return {
          success: true,
          message: data.message,
          email: data.email,
          alreadyVerified: data.alreadyVerified || false,
        };
      } catch (error) {
        return {
          success: false,
          message:
            error.response?.data?.message ||
            "Erro ao verificar email. O link pode estar expirado.",
        };
      }
    },

    // Resend verification email
    async resendVerification(email) {
      const toast = useToast();

      try {
        const data = await apiService.post(
          "/api/handlers?module=auth&action=resend-verification",
          { email }
        );

        toast.success(
          data.message || "Email de verificação reenviado com sucesso!"
        );
        return { success: true, message: data.message };
      } catch (error) {
        const message =
          error.response?.data?.message ||
          "Erro ao reenviar email de verificação.";
        toast.error(message);
        return { success: false, error: message };
      }
    },

    // Solicitar redefinição de senha
    async forgotPassword(email) {
      this.isLoading = true;
      try {
        const data = await apiService.post(
          "/api/handlers?module=auth&action=forgot-password",
          { email }
        );
        return { success: true, message: data.message };
      } catch (error) {
        const message =
          error.response?.data?.message || "Erro ao processar solicitação.";
        return { success: false, error: message };
      } finally {
        this.isLoading = false;
      }
    },

    // Redefinir senha com token
    async resetPassword(token, newPassword) {
      this.isLoading = true;
      try {
        const data = await apiService.post(
          "/api/handlers?module=auth&action=reset-password",
          { token, newPassword }
        );
        return { success: true, message: data.message };
      } catch (error) {
        const message =
          error.response?.data?.message || "Erro ao redefinir senha.";
        return { success: false, error: message };
      } finally {
        this.isLoading = false;
      }
    },
  },
});
